import React from 'react';
import { Button, Tooltip, Empty } from 'antd';
import { UndoOutlined } from '@ant-design/icons';

export type DiffLineType = 'equal' | 'added' | 'removed';
export interface DiffLine { type: DiffLineType; text: string; oldIdx?: number; newIdx?: number; }
export interface DiffHunk { lines: DiffLine[]; startIdx: number; }

export function computeLineDiff(oldText: string, newText: string): DiffLine[] {
    const oldLines = oldText.split('\n');
    const newLines = newText.split('\n');
    const n = oldLines.length;
    const m = newLines.length;

    const dp: number[][] = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
    for (let i = 1; i <= n; i++) {
        for (let j = 1; j <= m; j++) {
            dp[i][j] = oldLines[i - 1] === newLines[j - 1]
                ? dp[i - 1][j - 1] + 1
                : Math.max(dp[i - 1][j], dp[i][j - 1]);
        }
    }

    const result: DiffLine[] = [];
    let i = n, j = m;
    while (i > 0 || j > 0) {
        if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
            result.push({ type: 'equal', text: oldLines[i - 1], oldIdx: i - 1, newIdx: j - 1 });
            i--; j--;
        } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
            result.push({ type: 'added', text: newLines[j - 1], newIdx: j - 1 });
            j--;
        } else {
            result.push({ type: 'removed', text: oldLines[i - 1], oldIdx: i - 1 });
            i--;
        }
    }
    return result.reverse();
}

export function groupIntoHunks(diffLines: DiffLine[]): DiffHunk[] {
    const hunks: DiffHunk[] = [];
    let currentHunk: DiffLine[] = [];
    let hunkStart = 0;

    for (let i = 0; i < diffLines.length; i++) {
        if (diffLines[i].type !== 'equal') {
            if (currentHunk.length === 0) hunkStart = i;
            currentHunk.push(diffLines[i]);
        } else if (currentHunk.length > 0) {
            hunks.push({ lines: currentHunk, startIdx: hunkStart });
            currentHunk = [];
        }
    }
    if (currentHunk.length > 0) {
        hunks.push({ lines: currentHunk, startIdx: hunkStart });
    }
    return hunks;
}

export const DiffView: React.FC<{ diff: string }> = ({ diff }) => {
    if (!diff) return <Empty description="No changes" />;
    const lines = diff.split('\n');
    return (
        <pre style={{
            margin: 0,
            padding: 12,
            fontSize: 12,
            lineHeight: 1.7,
            fontFamily: "'SF Mono', 'Fira Code', monospace",
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
        }}>
            {lines.map((line, i) => {
                let bg = 'transparent';
                let color = '#24292f';
                if (line.startsWith('+') && !line.startsWith('+++')) { bg = '#dafbe1'; color = '#1a7f37'; }
                else if (line.startsWith('-') && !line.startsWith('---')) { bg = '#ffebe9'; color = '#cf222e'; }
                else if (line.startsWith('@@')) { bg = '#ddf4ff'; color = '#0969da'; }
                return (
                    <div key={i} style={{ background: bg, color, padding: '0 8px', margin: '0 -12px' }}>
                        {line || ' '}
                    </div>
                );
            })}
        </pre>
    );
};

export const LiveDiffView: React.FC<{
    savedText: string;
    draftText: string;
    onRevertHunk: (hunk: DiffHunk, allLines: DiffLine[]) => void;
}> = ({ savedText, draftText, onRevertHunk }) => {
    const diffLines = React.useMemo(() => computeLineDiff(savedText, draftText), [savedText, draftText]);
    const hunks = React.useMemo(() => groupIntoHunks(diffLines), [diffLines]);

    if (hunks.length === 0) {
        return <Empty description="No unsaved changes" style={{ padding: 40 }} />;
    }

    const lineStyle: React.CSSProperties = {
        fontSize: 12,
        lineHeight: 1.7,
        fontFamily: "'SF Mono', 'Fira Code', monospace",
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        padding: '0 8px',
        minHeight: 21,
    };

    return (
        <div style={{ overflow: 'auto', height: '100%' }}>
            <div style={{ display: 'flex', borderBottom: '1px solid #e8e8e8', padding: '6px 8px', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                <div style={{ flex: 1, fontWeight: 600, fontSize: 12, color: '#8c8c8c' }}>Saved</div>
                <div style={{ flex: 1, fontWeight: 600, fontSize: 12, color: '#8c8c8c' }}>Draft</div>
            </div>
            {diffLines.map((line, i) => {
                const hunkForLine = hunks.find(
                    (h) => i >= h.startIdx && i < h.startIdx + h.lines.length,
                );
                const isHunkStart = hunkForLine && i === hunkForLine.startIdx;

                return (
                    <div key={i} style={{ position: 'relative' }}>
                        {isHunkStart && (
                            <div style={{ position: 'absolute', right: 4, top: 0, zIndex: 1 }}>
                                <Tooltip title="Revert this change">
                                    <Button
                                        size="small"
                                        type="text"
                                        icon={<UndoOutlined />}
                                        onClick={() => onRevertHunk(hunkForLine, diffLines)}
                                        style={{ fontSize: 11, opacity: 0.6 }}
                                    />
                                </Tooltip>
                            </div>
                        )}
                        <div style={{ display: 'flex' }}>
                            <div style={{
                                flex: 1,
                                ...lineStyle,
                                background: line.type === 'removed' ? '#ffebe9' : 'transparent',
                                color: line.type === 'removed' ? '#cf222e' : '#24292f',
                            }}>
                                {line.type === 'removed' ? line.text : line.type === 'equal' ? line.text : ''}
                                {line.type !== 'equal' && line.type !== 'removed' && '\u00A0'}
                            </div>
                            <div style={{ width: 1, background: '#f0f0f0', flexShrink: 0 }} />
                            <div style={{
                                flex: 1,
                                ...lineStyle,
                                background: line.type === 'added' ? '#dafbe1' : 'transparent',
                                color: line.type === 'added' ? '#1a7f37' : '#24292f',
                            }}>
                                {line.type === 'added' ? line.text : line.type === 'equal' ? line.text : ''}
                                {line.type !== 'equal' && line.type !== 'added' && '\u00A0'}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
